import React from 'react'
import { Link } from 'react-router-dom'

export default function AlternateHomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Your Salesforce Flows Have Hidden Problems
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            FlowDocs analyzes every flow in seconds, finding critical issues before they hurt your users
          </p>
          <a
            href="https://forms.google.com/your-form-link"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors"
          >
            Join 31 Salesforce Pros in Beta
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            The Hidden Risks in Your Org
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-lg text-gray-700 font-medium">
                "You inherit 171 flows. Which ones are broken?"
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-lg text-gray-700 font-medium">
                "Users report errors, but which flow is failing?"
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-lg text-gray-700 font-medium">
                "Need SOX compliance docs. Where do you start?"
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img 
              src="/screenshots/Dashboard Issue Distribution widget.png" 
              alt="Dashboard showing 43 Incomplete Flows, 12 Critical Issues"
              className="w-full h-auto"
            />
            <div className="p-4 bg-gray-50 text-center text-sm text-gray-600">
              Dashboard showing 43 Incomplete Flows, 12 Critical Issues
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Know What Needs Fixing - And Why
          </h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img 
              src="/screenshots/Dashboard Issue Distribution widget.png" 
              alt="Issue Distribution Chart"
              className="w-full h-auto"
            />
            <div className="p-6 text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <div className="text-lg font-semibold text-gray-700">74 Missing Error Handling</div>
                <div className="text-lg font-semibold text-gray-700">43 Hardcoded IDs</div>
                <div className="text-lg font-semibold text-gray-700">1 Query in Loop</div>
              </div>
              <p className="mt-4 text-gray-600">
                ☕ FlowDocs analyzed 171 flows during your coffee break
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Discovery Journey */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            From Problem to Solution in Minutes
          </h2>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 bg-purple-50 border-b border-purple-100">
                <h3 className="font-semibold text-lg text-purple-900">1. Filter & Find</h3>
              </div>
              <img 
                src="/screenshots/Queries in Loops.png" 
                alt="Flows list filtered by Queries in Loops"
                className="w-full h-64 object-cover bg-gray-100"
              />
              <div className="p-4">
                <p className="text-sm text-gray-600">Flows list filtered by "Queries in Loops"</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 bg-purple-50 border-b border-purple-100">
                <h3 className="font-semibold text-lg text-purple-900">2. Understand Why</h3>
              </div>
              <img 
                src="/screenshots/Query in Loop Flow Scorecard.png" 
                alt="Grading details showing -30 deduction"
                className="w-full h-64 object-cover bg-gray-100"
              />
              <div className="p-4">
                <p className="text-sm text-gray-600">Grading details showing -30 deduction</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 bg-purple-50 border-b border-purple-100">
                <h3 className="font-semibold text-lg text-purple-900">3. See the Issue</h3>
              </div>
              <img 
                src="/screenshots/Query in Loop SF Flow.png" 
                alt="Flow Builder showing the problematic loop"
                className="w-full h-64 object-cover bg-gray-100"
              />
              <div className="p-4">
                <p className="text-sm text-gray-600">Flow Builder showing the problematic loop</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unique Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            What Salesforce Can't Show You
          </h2>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Field Impact Analysis */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Field Impact Analysis</h3>
              <p className="text-gray-600 mb-6">
                "Where is Task.Priority used? Salesforce won't tell you (it's a standard field)"
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">Before:</span>
                  <span className="text-red-600">"Check each flow manually"</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">After:</span>
                  <span className="text-green-600">"15 flows found instantly"</span>
                </div>
              </div>
              <img 
                src="/screenshots/Fields - Field Impact - Priority.png" 
                alt="Field Impact showing Task.Priority"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            
            {/* Complete Documentation */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Complete Documentation</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">AI summaries in plain English</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Best practices library</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">Step-by-step fix guides</span>
                </li>
              </ul>
              <img 
                src="/screenshots/Hardcoded IDs Help Article.png" 
                alt="Hardcoded IDs best practice guide"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Builders */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Help Center Preview
            </h3>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h4 className="text-xl font-medium text-gray-900 mb-4">5 Best Practice Guides</h4>
                <p className="text-gray-600 mb-4">Growing library of solutions</p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2">A</span>
                    <span className="text-sm text-gray-700">Top performing flows</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2">B</span>
                    <span className="text-sm text-gray-700">Good with minor issues</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">C</span>
                    <span className="text-sm text-gray-700">Needs attention</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 mr-2">D</span>
                    <span className="text-sm text-gray-700">Critical issues</span>
                  </div>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-2">I</span>
                    <span className="text-sm text-gray-700">Incomplete</span>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="/screenshots/Help Center - Home.png" 
                  alt="Help Center hub"
                  className="w-full rounded-lg shadow"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to see what's hiding in your flows?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            First 10 users get personal onboarding
          </p>
          <a
            href="https://forms.google.com/your-form-link"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-purple-600 bg-white hover:bg-gray-50 transition-colors"
          >
            Get Beta Access
          </a>
        </div>
      </section>
    </div>
  )
}